import { useState, useEffect, useCallback, useRef } from 'react';
const spiliosSpiliopoulos = "/images/spilios-spiliopoulos.jpg";

interface LoadingScreenProps {
  onLoadComplete: () => void;
}

// Preload Cal.com script immediately when this module loads
const preloadCalScript = () => {
  if (typeof window === 'undefined') return;
  if (window.Cal) return; // Already loaded

  (function (C: Window & { Cal?: any }, A: string, L: string) {
    let p = function (a: any, ar: any) {
      a.q.push(ar);
    };
    let d = C.document;
    C.Cal =
      C.Cal ||
      function () {
        let cal = C.Cal;
        let ar = arguments;
        if (!cal.loaded) {
          cal.ns = {};
          cal.q = cal.q || [];
          const script = d.createElement("script");
          script.src = A;
          script.async = true;
          d.head.appendChild(script);
          cal.loaded = true;
        }
        if (ar[0] === L) {
          const api = function () {
            p(api, arguments);
          };
          const namespace = ar[1];
          (api as any).q = (api as any).q || [];
          if (typeof namespace === "string") {
            cal.ns[namespace] = cal.ns[namespace] || api;
            p(cal.ns[namespace], ar);
            p(cal, ["initNamespace", namespace]);
          } else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
  })(window as any, "https://app.cal.com/embed/embed.js", "init");

  // Initialize Cal.com
  (window as any).Cal("init", "30min", { origin: "https://app.cal.com" });
};

// Start preloading immediately
preloadCalScript();

const LoadingScreen = ({ onLoadComplete }: LoadingScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('Initializing...');
  const [isExiting, setIsExiting] = useState(false);
  const startTimeRef = useRef(Date.now());
  const MIN_LOADING_TIME = 1500; // Minimum time to show loader for smooth UX

  const preloadImage = useCallback((src: string): Promise<void> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve();
      img.onerror = () => resolve(); // Don't block on error
      img.src = src;
      // Timeout fallback
      setTimeout(resolve, 3000);
    });
  }, []);

  const waitForCalendar = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const checkCalReady = () => {
        // Check if Cal is loaded AND the inline element exists
        const calElement = document.getElementById('my-cal-inline-30min');
        const hasIframe = calElement?.querySelector('iframe');

        if ((window as any).Cal?.loaded && hasIframe) {
          resolve();
          return true;
        }
        return false;
      };

      if (checkCalReady()) return;

      // Poll for Cal.com to be fully rendered
      let attempts = 0;
      const maxAttempts = 60; // 6 seconds max
      const interval = setInterval(() => {
        attempts++;
        if (checkCalReady() || attempts >= maxAttempts) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }, []);

  const preloadFonts = useCallback(async (): Promise<void> => {
    if ('fonts' in document) {
      try {
        await Promise.race([
          document.fonts.ready,
          new Promise(resolve => setTimeout(resolve, 2000))
        ]);
      } catch {
        // Font loading not critical
      }
    }
  }, []);

  const waitForDOMReady = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      // Check if key elements are rendered
      const checkElements = () => {
        const hero = document.getElementById('hero') || document.querySelector('section');
        const nav = document.querySelector('nav');
        return hero && nav;
      };

      if (checkElements()) {
        resolve();
        return;
      }

      // Poll for elements
      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (checkElements() || attempts >= 30) {
          clearInterval(interval);
          resolve();
        }
      }, 100);
    });
  }, []);

  useEffect(() => {
    const loadAssets = async () => {
      // Run preloading tasks in parallel for efficiency
      const parallelTasks = [
        preloadFonts(),
        preloadImage(spiliosSpiliopoulos),
      ];

      setLoadingText('Loading assets...');
      setProgress(10);

      // Wait for parallel tasks
      await Promise.all(parallelTasks);
      setProgress(40);

      // Wait for DOM to be ready
      setLoadingText('Preparing interface...');
      await waitForDOMReady();
      setProgress(60);

      // Wait for calendar
      setLoadingText('Loading calendar...');
      await waitForCalendar();
      setProgress(90);

      // Ensure minimum loading time for smooth UX
      const elapsed = Date.now() - startTimeRef.current;
      if (elapsed < MIN_LOADING_TIME) {
        setLoadingText('Almost ready...');
        await new Promise(resolve => setTimeout(resolve, MIN_LOADING_TIME - elapsed));
      }

      setProgress(100);
      setLoadingText('Ready!');

      // Smooth exit animation
      await new Promise(resolve => setTimeout(resolve, 300));
      setIsExiting(true);
      await new Promise(resolve => setTimeout(resolve, 500));
      onLoadComplete();
    };

    loadAssets();
  }, [preloadImage, preloadFonts, waitForCalendar, waitForDOMReady, onLoadComplete]);

  return (
    <div
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#080510] transition-all duration-500 ${
        isExiting ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
      }`}
    >
      {/* Animated galaxy rings background */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border opacity-20"
            style={{
              width: `${200 + i * 150}px`,
              height: `${(200 + i * 150) * 0.35}px`,
              borderColor: `hsl(${245 + i * 10}, 80%, 60%)`,
              animation: `pulse ${2 + i * 0.3}s ease-in-out infinite`,
              animationDelay: `${i * 0.2}s`,
            }}
          />
        ))}
      </div>

      {/* Center glow */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-gradient-radial from-violet-500/20 via-violet-500/10 to-transparent rounded-full blur-3xl animate-pulse" />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-8">
        {/* Logo / Name */}
        <div className="text-center space-y-2">
          <div className="text-4xl md:text-5xl font-bold" aria-hidden="true">
            <span className="text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.6)]">
              Spilios
            </span>{' '}
            <span className="text-violet-400 [text-shadow:0_0_30px_rgba(167,139,250,0.6)]">
              Spiliopoulos
            </span>
          </div>
          <p className="text-muted-foreground text-sm tracking-widest uppercase">
            CTO & AI Architect
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-64 md:w-80 space-y-3">
          <div className="h-1 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
            <div
              className="h-full bg-gradient-to-r from-violet-500 via-violet-500 to-violet-500 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span className="animate-pulse">{loadingText}</span>
            <span>{Math.round(progress)}%</span>
          </div>
        </div>

        {/* Orbiting dots */}
        <div className="relative w-16 h-16">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute w-2 h-2 bg-violet-400 rounded-full"
              style={{
                animation: `orbit ${1.5 + i * 0.3}s linear infinite`,
                animationDelay: `${i * 0.2}s`,
                boxShadow: '0 0 10px rgba(167, 139, 250, 0.8)',
              }}
            />
          ))}
        </div>
      </div>

      <style>{`
        @keyframes orbit {
          from {
            transform: rotate(0deg) translateX(24px) rotate(0deg);
          }
          to {
            transform: rotate(360deg) translateX(24px) rotate(-360deg);
          }
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 0.1;
            transform: translate(-50%, -50%) scale(1);
          }
          50% {
            opacity: 0.25;
            transform: translate(-50%, -50%) scale(1.05);
          }
        }
      `}</style>
    </div>
  );
};

export default LoadingScreen;
